import { filter, forEach, includes, get, keyBy, compose } from 'lodash/fp';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getRepositoryQueryKey, Notification, NotificationResult } from '@mews-commander/data-access';
import { NotificationState } from '@mews-commander/legacy-data-structures';
import { createFetchAction, notNil } from '@mews-commander/utils';
import { getCurrentScopeProfileId } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { fetchPaymentsByIds } from '@mews-commander/legacy-entities/src/actions/payments';
import prefix from '../prefix';
import { getKnownNotificationIds } from '../selectors';
import { isSeen } from '../utils/isSeen';
import { fetchThreadData } from '../../messages/actions/threads';
import { isMessageModuleVisible } from '../../messages/selectors/utils';
import { showAlert } from '../../app/components/TemplateFetcher/utils';

export const NOTIFICATIONS_FETCHED = prefix('NOTIFICATIONS_FETCHED');
export const NOTIFICATION_FETCHED = prefix('NOTIFICATION_FETCHED');
export const NOTIFICATION_MARKED_SEEN = prefix('NOTIFICATION_MARKED_SEEN');
export const NOTIFICATION_MARKED_PENDING = prefix('NOTIFICATION_MARKED_PENDING');

const hasFlashType = compose(notNil, get('flashType'));

const transformNotifications =
    ({ data, displayFlash }: { data: NotificationResult; displayFlash: boolean }): CommanderThunkAction =>
    (dispatch, getState, { reactQueryClient }) => {
        const items = data.notifications;
        const state = getState();

        const unknownNotifications = getUnknownNotifications(items, getKnownNotificationIds(state));

        if (displayFlash) {
            const currentProfileId = getCurrentScopeProfileId(state);
            const notificationsWithFlashForCurrentProfile = filter(
                (n) => hasFlashType(n) && (n as $TSFixMe).profileId === currentProfileId
            )(unknownNotifications);
            forEach(({ command, accountId, flashType, title, id, profileId }: Notification) => {
                showAlert({
                    message: title ?? '',
                    type: flashType,
                });

                if (accountId && command?.paymentId) {
                    void reactQueryClient.invalidateQueries({
                        queryKey: getRepositoryQueryKey('payments', 'getTableRecords', { accountId }),
                    });

                    dispatch(markNotificationsSeen([id], profileId));
                }

                if (accountId && command?.preauthorizationId) {
                    void reactQueryClient.invalidateQueries({
                        queryKey: getRepositoryQueryKey('preauthorizations', 'getTableRecords', { accountId }),
                    });
                    void reactQueryClient.invalidateQueries({
                        queryKey: getRepositoryQueryKey('accountChargeablePreauthorizations', 'getAll', { accountId }),
                    });

                    dispatch(markNotificationsSeen([id], profileId));
                }

                if (command?.paymentId && accountId != null) {
                    void dispatch(
                        fetchPaymentsByIds({
                            ids: [command.paymentId],
                            accountId,
                            extent: { paymentCards: true },
                        })
                    );
                }
            })(notificationsWithFlashForCurrentProfile);
        }

        dispatch(createAction(NOTIFICATIONS_FETCHED, keyBy('id')(items)));
    };

export const fetchNotifications =
    () =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .notificationComponent.getNotifications()
            .then((data: $TSFixMe) => dispatch(transformNotifications({ data, displayFlash: false })));

        return createFetchAction(NOTIFICATION_FETCHED, promise);
    };

export const fetchNotificationsByIds =
    (ids: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container()
            .notificationComponent.getNotificationByIds(ids)
            .then((data: NotificationResult) => {
                const { notifications } = data;
                const state = getState();
                if (isMessageModuleVisible(state)) {
                    const currentProfileId = getCurrentScopeProfileId(state);
                    compose(
                        forEach((n) => {
                            const threadId = get('message.threadId')(n);
                            if (threadId) {
                                dispatch(fetchThreadData(threadId));
                            }
                        }),
                        filter({ isSeen: false, profileId: currentProfileId })
                    )(notifications);
                }

                return data;
            })
            .then((data: NotificationResult) => dispatch(transformNotifications({ data, displayFlash: true })));

        return createFetchAction(NOTIFICATION_FETCHED, promise);
    };

export function markSeen(notificationId: $TSFixMe) {
    return createAction(NOTIFICATION_MARKED_SEEN, notificationId);
}

export function markPending(notificationId: $TSFixMe) {
    return createAction(NOTIFICATION_MARKED_PENDING, notificationId);
}

export function markNotificationsSeen(notificationIds: $TSFixMe, profileId: $TSFixMe) {
    return markAsSeen(notificationIds, profileId);
}

function getUnknownNotifications(items: $TSFixMe, known: $TSFixMe) {
    return filter((item) => !isSeen(item) && !includes((item as $TSFixMe).id, known))(items);
}

function markAsSeen(ids: $TSFixMe, profileId: $TSFixMe) {
    return (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        forEach((id) => {
            dispatch(createAction(NOTIFICATION_MARKED_SEEN, id));
        })(ids);

        return container().notificationComponent.updateState(ids, NotificationState.Seen, profileId);
    };
}
